import type { DirectorySearch } from "./search-params";
import { ServiceType } from "@complete-athlete/enums";

/** What the service segment says on a city's page with every service in it. */
export const ALL_SERVICES = "all";

/** A city as an address: "Nowy Sącz" is `nowy-sacz`. */
export const citySlug = (city: string): string =>
  city
    .normalize("NFKD")
    .replaceAll(/\p{Mark}/gu, "")
    .toLowerCase()
    .replaceAll("ł", "l")
    .replaceAll(/[^a-z0-9]+/g, "-")
    .replaceAll(/^-+|-+$/g, "");

/** The address of a service, a city or both, which is a page of its own for search engines (ADR 0042). */
export const searchPagePath = ({ service, city }: { service?: ServiceType; city?: string }): string => {
  if (city) return `/search/${service ?? ALL_SERVICES}/${citySlug(city)}`;
  return service ? `/search/${service}` : "/search";
};

/** Free words, a position or a sort: the visitor's own view, which lives in the query string. */
export const isOwnView = (search: DirectorySearch & { sort?: string }): boolean =>
  Boolean(search.q || search.near || search.online || search.sort);

/** The service an address names, or undefined when it names none we offer. */
export const serviceFromPath = (segment: string): ServiceType | undefined =>
  Object.values(ServiceType).find((service) => service === segment);

/** The city an address names, among those someone is listed in. */
export const cityFromPath = <City extends { city: string }>(segment: string, cities: City[]): City | undefined => {
  const slug = citySlug(segment);
  return cities.find((entry) => citySlug(entry.city) === slug);
};

/**
 * Where a search belongs when it is one of the directory's pages: a search typed into `/search`
 * moves there for good. Null when it stays put: it is the visitor's own view, it is `/search`
 * itself, or it names a city nobody is listed in, which has no page but still gets an answer.
 */
export const cleanSearchPath = (
  search: DirectorySearch & { sort?: string },
  cities: Array<{ city: string }>,
): string | null => {
  if (isOwnView(search) || (!search.service && !search.city)) return null;
  if (search.city && !cityFromPath(search.city, cities)) return null;
  return searchPagePath(search);
};
